import { z } from 'zod';
import { zAddress, zObjectId } from './primitives.js';
/**
 * Calls: contract addresses posted by callers (people in a Telegram channel). Every call is stored
 * once with a snapshot of the token at that moment (price, market cap, liquidity) that is never
 * refreshed: Analytics answers "what was it worth when they called it". Callers and calls are
 * shared by every account; each account chooses which callers it snipes.
 */
/**
 * Where a call came from: `telegram` (the channel reader), `manual` (an admin posted it from the
 * Telegram page), `mock` (the development generator; sniped by paper accounts only).
 */
export const CALL_SOURCES = ['telegram', 'manual', 'mock'];
/** A caller's handle as posted in the channel, without the leading "@". */
export const zCallerHandle = z
    .string()
    .trim()
    .transform((value) => value.replace(/^@+/, ''))
    .pipe(z
    .string()
    .min(1, 'Give the caller a handle')
    .max(64, 'Keep the handle under 64 characters')
    .regex(/^[\p{L}\p{N}_.\- ]+$/u, 'Letters, digits, spaces, "_", "." and "-" only'));
/**
 * One call as the source delivers it (`POST /api/calls/ingest`). `messageId` makes redelivery
 * harmless: the same source message is stored once.
 */
export const zIngestCall = z.object({
    caller: zCallerHandle,
    mint: zAddress,
    /** When the caller posted it (defaults to when it was received). */
    calledAt: z.coerce.date().optional(),
    source: z.enum(CALL_SOURCES).default('telegram'),
    /** The source's own id for the message (e.g. "<chat id>:<message id>"). */
    messageId: z.string().trim().min(1).max(128).optional(),
    /**
     * The source's stable id for the caller (e.g. "tg:<user id>"): a caller who changes their handle
     * stays one caller. Without it, callers are told apart by handle.
     */
    externalId: z.string().trim().min(1).max(64).optional(),
});
/** An admin posts a call by hand (stands in for the channel until its reader is connected). */
export const zPostCall = z.object({ caller: zCallerHandle, mint: zAddress });
/** Calls older than this when they arrive are stored for Analytics but never sniped. */
export const SNIPE_MAX_CALL_AGE_MS = 120_000;
/** Analytics list: newest first, filtered by caller, period and a search over CA, name and symbol. */
export const CALL_PERIODS = ['24h', '7d', '30d', 'all'];
export const zCallsQuery = z.object({
    callerId: zObjectId.optional(),
    period: z.enum(CALL_PERIODS).default('all'),
    q: z.string().trim().max(64).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
/* ------------------------------------------------------------------------------------------------
 * The Telegram bot (set up by an admin in Admin › Platform).
 * ---------------------------------------------------------------------------------------------- */
/** A bot token from @BotFather: "<bot id>:<secret>". */
export const zTelegramBotToken = z
    .string()
    .trim()
    .regex(/^\d{5,}:[\w-]{30,}$/, 'Paste the token @BotFather gave you (it looks like 123456789:AA…)');
/** A Telegram chat id: groups are negative ("-1001234567890"). */
export const zTelegramChatId = z
    .string()
    .trim()
    .regex(/^-?\d{5,20}$/, 'Chat ids are numbers like -1001234567890');
/** `botToken: null` removes the token (the reader stops); leaving it out keeps the stored one. */
export const zUpdateTelegramSettings = z
    .object({
    enabled: z.boolean(),
    botToken: zTelegramBotToken.nullable(),
    chatIds: z.array(zTelegramChatId).max(20, 'Up to 20 chats'),
})
    .partial();
/* ------------------------------------------------------------------------------------------------
 * Snipe: buy what a caller calls, the moment it arrives.
 * ---------------------------------------------------------------------------------------------- */
export const zSnipeBuySol = z
    .number({ message: 'Enter an amount in SOL' })
    .min(0.001, 'At least 0.001 SOL')
    .max(100, 'At most 100 SOL');
export const DEFAULT_SNIPE_BUY_SOL = 0.1;
export const zUpdateCallerSnipe = z
    .object({
    enabled: z.boolean(),
    strategyId: zObjectId.nullable(),
    buySol: zSnipeBuySol,
})
    .partial();
/**
 * What a snipe did: `pending` (the engine is on it), `bought` (filled live), `simulated` (paper
 * fill), `sent` (landing not confirmed yet), `skipped` (a rule said no: `reason` + `detail`),
 * `failed` (the buy itself failed).
 */
export const SNIPE_STATUSES = ['pending', 'bought', 'simulated', 'sent', 'skipped', 'failed'];
/** Snipe-only skip reasons; the rest are the copy engine's (`SKIP_REASON_LABEL`). */
export const SNIPE_REASON_LABEL = {
    mock_call_live: 'Sample calls only snipe paper accounts',
    engine_unavailable: 'The engine could not be reached',
};
//# sourceMappingURL=calls.js.map